import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTenant } from '../../context/TenantContext';
import { Button } from './button';
import PrivacyPolicyIcon from '../../assets/images/icons/cookies-icon.png';

/**
 * Cookie Consent Banner (replaces modal)
 * 
 * Features:
 * - Top banner (non-intrusive)
 * - Three buttons: Accept All, Deny, Review Choices
 * - Expandable section for granular controls
 * - Closeable with corner icon to reopen
 * - Extensible for additional cookie categories
 * 
 * Props:
 * - show (bool) - whether banner is visible
 * - onClose (fn) - called when user closes banner
 * - onAccept (fn) - called when user accepts all
 * - onDeny (fn) - called when user denies analytics
 * - onSaveGranular (fn) - called when user saves granular choices
 */
const CookieBanner = ({ show, onClose, onAccept, onDeny, onSaveGranular }) => {
  const { t } = useLanguage();
  const { tenant } = useTenant();
  
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [granularChoices, setGranularChoices] = useState({
    essential: true,
    analytics: false,
    // Future categories can be added here:
    // marketing: false,
    // functional: false,
  });

  // Don't render if not showing
  if (!show) return null;

  // Get cookie config from tenant
  const cookieConfig = tenant?.tenantcontentconfig?.cookies || {};
  const policyUrl = cookieConfig.cookiePolicyUrl || 
                    cookieConfig.cookie?.link || 
                    'https://www.visa.com.sg/legal/global-privacy-notice/cookie-notice.html';
  const policyText = cookieConfig.cookiePolicyLinkText || 
                     t('cookieModal.readPolicy') || 
                     'Cookie Policy';

  const handleAccept = () => {
    setExpanded(false);
    setMinimized(false);
    onAccept();
  };

  const handleDeny = () => {
    setExpanded(false);
    setMinimized(false);
    onDeny();
  };

  const handleReviewChoices = () => {
    setExpanded(!expanded);
  };

  const handleSaveGranular = () => {
    setExpanded(false);
    setMinimized(false);
    onSaveGranular(granularChoices);
  };

  const handleCancel = () => {
    setExpanded(false);
    // Reset to default
    setGranularChoices({
      essential: true,
      analytics: false,
    });
  };

  const handleMinimize = () => {
    setMinimized(true);
  };

  const handleReopen = () => {
    setMinimized(false);
  };

  const toggleAnalytics = () => {
    setGranularChoices(prev => ({
      ...prev,
      analytics: !prev.analytics
    }));
  };

  // Minimized corner icon
  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-[9999]">
        <button
          onClick={handleReopen}
          className="bg-theme-primary hover:bg-theme-primary/90 text-white p-3 rounded-full shadow-lg flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 transition-all hover:scale-110"
          aria-label={t('cookieSettings.title') || 'Cookie Settings'}
          title={t('cookieSettings.title') || 'Cookie Settings'}
        >
          <img 
            src={PrivacyPolicyIcon} 
            alt="" 
            aria-hidden="true"
            className="w-6 h-6 object-contain"
          />
        </button>
      </div>
    );
  }

  // Full banner
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] bg-white shadow-lg border-b-2 border-gray-200"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Banner Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            {/* Cookie Icon */}
            <div className="flex-shrink-0">
              <img 
                src={PrivacyPolicyIcon} 
                alt="" 
                aria-hidden="true"
                className="w-12 h-12 object-contain"
              />
            </div>
            
            {/* Text Content */}
            <div className="flex-1 min-w-0">
              <h2 
                id="cookie-banner-title" 
                className="text-base font-bold text-theme-secondary mb-1"
              >
                {t('cookieModal.title') || 'Your Privacy Matters'}
              </h2>
              <p 
                id="cookie-banner-description" 
                className="text-sm text-gray-600 mb-2"
              >
                {t('cookieModal.description') || 
                 'We use cookies to enhance your experience and analyse how our site is used.'}
              </p>
              <a
                href={policyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-theme-primary hover:text-blue-700 underline text-sm font-medium focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 rounded px-1"
              >
                {policyText}
              </a>
            </div>
          </div>

          {/* Close/Minimize Button */}
          <button
            onClick={handleMinimize}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded p-1"
            aria-label={t('common.close') || 'Close banner'}
          >
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        </div>

        {/* Action Buttons (Always Visible) */}
        <div className="mt-4 flex flex-wrap sm:flex-nowrap items-center gap-2 sm:justify-end">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleAccept}
            className="w-full sm:w-auto rounded-full h-9 px-6 text-xs font-medium focus:ring-2 focus:ring-theme-primary focus:ring-offset-2"
          >
            {t('cookieModal.acceptAll') || 'Accept All'}
          </Button>
          
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleDeny}
            className="w-full sm:w-auto rounded-full h-9 px-6 text-xs font-medium focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            {t('cookieModal.deny') || 'Deny'}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleReviewChoices}
            className="w-full sm:w-auto rounded-full h-9 px-6 text-xs font-medium focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 flex items-center justify-center gap-1"
          >
            {t('cookieSettings.title') || 'Review Choices'}
            <svg 
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 9l-7 7-7-7" 
              />
            </svg>
          </Button>
        </div>

        {/* Expandable Granular Controls */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 animate-in slide-in-from-top duration-300 max-h-[50vh] overflow-y-auto pb-4">
            <h3 className="text-sm font-semibold text-theme-secondary mb-3">
              {t('cookieSettings.description') || 'Manage Your Cookie Preferences'}
            </h3>
            
            <div className="space-y-3">
              {/* Essential Cookies - Always On */}
              <div className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1 pr-4">
                  <h4 className="text-sm font-semibold text-theme-secondary mb-1">
                    {t('cookieSettings.essential.label') || 'Essential Cookies'}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('cookieSettings.essential.description') || 
                     'Required for the website to function. Cannot be disabled.'}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-block px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                    {t('cookieSettings.essential.alwaysOn') || 'Always On'}
                  </span>
                </div>
              </div>

              {/* Analytics Cookies - Toggleable */}
              <div className="flex items-start justify-between p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex-1 pr-4">
                  <h4 className="text-sm font-semibold text-theme-secondary mb-1">
                    {t('cookieSettings.analytics.label') || 'Analytics Cookies'}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('cookieSettings.analytics.description') || 
                     'Help us understand how you use our website to improve your experience.'}
                  </p>
                  <span 
                    className={`inline-block mt-2 text-xs font-medium ${
                      granularChoices.analytics ? 'text-green-600' : 'text-gray-500'
                    }`}
                  >
                    {granularChoices.analytics 
                      ? (t('cookieSettings.analytics.on') || 'Enabled')
                      : (t('cookieSettings.analytics.off') || 'Disabled')
                    }
                  </span>
                </div>
                <div className="flex-shrink-0">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={granularChoices.analytics}
                    aria-label={`${t('cookieSettings.analytics.label') || 'Analytics Cookies'}. ${
                      granularChoices.analytics 
                        ? (t('cookieSettings.analytics.on') || 'Currently enabled')
                        : (t('cookieSettings.analytics.off') || 'Currently disabled')
                    }`}
                    onClick={toggleAnalytics}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 ${
                      granularChoices.analytics ? 'bg-theme-primary' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        granularChoices.analytics ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Future categories can be added here:
              
              <div className="flex items-start justify-between p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex-1 pr-4">
                  <h4 className="text-sm font-semibold text-theme-secondary mb-1">
                    Marketing Cookies
                  </h4>
                  <p className="text-xs text-gray-600">
                    Used to track visitors across websites for advertising purposes.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  [Toggle switch component]
                </div>
              </div>
              */}
            </div>

            {/* Granular Control Actions */}
            <div className="mt-4 flex flex-wrap sm:flex-nowrap items-center gap-2 sm:justify-end">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleSaveGranular}
                className="w-full sm:w-auto rounded-full h-9 px-6 text-xs font-medium focus:ring-2 focus:ring-theme-primary focus:ring-offset-2"
              >
                {t('cookieSettings.saveButton') || 'Save Preferences'}
              </Button>
              
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleCancel}
                className="w-full sm:w-auto rounded-full h-9 px-6 text-xs font-medium focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                {t('cookieSettings.cancelButton') || 'Cancel'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
